import { Button, Col, Divider, Form, InputNumber, Modal, ModalProps, notification, Radio, Row, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import GrainReconditioningOperationRequestDTO from "src/models/GrainReconditioningOperationRequestDTO";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { getEnumDictionary, getGrainTypeStr } from "src/utils/enumUtils";
import GrainType from "src/consts/GrainType";
import { convertCelsiusToFahrenheit, convertCelsiusToFahrenheitOffset, convertFahrenheitToCelsius, convertFahrenheitToCelsiusOffset } from "src/utils/generalUtils";
import BinApiService from "src/api/BinApiService";

const { Text, Title } = Typography;
const { Option } = Select;

interface PremierPlusReconditioningSettingsModalProps extends ModalProps {
    binDto: PremierPlusBinDTO | null;
    onClose: (result: any) => void;
}

const PremierPlusReconditioningSettingsModal : React.FC<PremierPlusReconditioningSettingsModalProps> = (props) => {

    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm<GrainReconditioningOperationRequestDTO>();
    const values = Form.useWatch([], form);

    const grainOptions = getEnumDictionary(GrainType);
    const useCelsius = props.binDto?.platformSettings?.useMetricSystem;

    const temperatureUnit = useCelsius ? "°C" : "°F";
    const tempButtonStep = useCelsius ? 1 : 2;

    const toDisplayTemperature = (value: number | null | undefined) =>
        useCelsius ? convertFahrenheitToCelsius(value) : value ?? null;

    const toServerTemperature = (value: number | null | undefined) =>
        useCelsius ? convertCelsiusToFahrenheit(value) : value ?? null;

    const toDisplayTemperatureOffset = (value: number | null | undefined) =>
        useCelsius ? convertFahrenheitToCelsiusOffset(value) : value ?? null;

    const toServerTemperatureOffset = (value: number | null | undefined) =>
        useCelsius ? convertCelsiusToFahrenheitOffset(value) : value ?? null;

    const reconditioningSettings = props.binDto?.settings?.reconditioningSettings;

    useEffect(() => {

        if(form.isFieldsTouched()){
            return;
        }

        form.setFieldValue('grainType', props.binDto?.settings?.dryerSettings?.grainType);
        form.setFieldValue('targetStorageBinNumber', reconditioningSettings?.targetStorageBinNumber);

        form.setFieldValue('samplesCntInAverage', reconditioningSettings?.samplesCntInAverage);
        form.setFieldValue('targetMcPercent', reconditioningSettings?.targetMcPercent);
        form.setFieldValue('disableMoistureChangingCheck', reconditioningSettings?.disableMoistureChangingCheck);

        form.setFieldValue('useTimer', reconditioningSettings?.useTimer);
        form.setFieldValue('runFanHours', reconditioningSettings?.runFanHours);
        form.setFieldValue('runFanMinutes', reconditioningSettings?.runFanMinutes);

        form.setFieldValue('useEmcRange', reconditioningSettings?.useEmcRange);
        form.setFieldValue('minWeatherMc', reconditioningSettings?.minWeatherMc);
        form.setFieldValue('maxWeatherMc', reconditioningSettings?.maxWeatherMc);

        form.setFieldValue('useTempRange', reconditioningSettings?.useTempRange);
        form.setFieldValue('minWeatherTemperatureF', toDisplayTemperature(reconditioningSettings?.minWeatherTemperatureF));
        form.setFieldValue('maxWeatherTemperatureF', toDisplayTemperature(reconditioningSettings?.maxWeatherTemperatureF));

        form.setFieldValue('grainMcCalibrationPercent', reconditioningSettings?.grainMcCalibrationPercent);
        form.setFieldValue('grainTempCalibrationF', toDisplayTemperatureOffset(reconditioningSettings?.grainTempCalibrationF));
        form.setFieldValue('plenumTempCalibrationF', toDisplayTemperatureOffset(reconditioningSettings?.plenumTempCalibrationF));

    }, [form, props.binDto, useCelsius]);


    const onFinish = async (data: GrainReconditioningOperationRequestDTO) =>
    {
        try{
            setIsLoading(true);
            const payload: GrainReconditioningOperationRequestDTO = {
                ...data,
                minWeatherTemperatureF: toServerTemperature(data.minWeatherTemperatureF) ?? 0,
                maxWeatherTemperatureF: toServerTemperature(data.maxWeatherTemperatureF) ?? 0,
                grainTempCalibrationF: toServerTemperatureOffset(data.grainTempCalibrationF) ?? 0,
                plenumTempCalibrationF: toServerTemperatureOffset(data.plenumTempCalibrationF) ?? 0,
            };
            try {
                await BinApiService.premierPlus_UpdateReconditioningOperation(props.binDto?.binId ?? 0, payload);
                props.onClose(null);
            } catch (err) {
                notification.error({ message: "Could not set reconditioning settings" });
                console.error(err);
            }
        }catch(error){
            notification.error({ message: "Could not set reconditioning settings" });
            console.error(error);
        }finally{
            setIsLoading(false);
        }
    }

    const handleClose = () => {
        props.onClose(null);
    };

    const isReconditioningActive = props.binDto?.reconditioningOperation?.isReconditioningRunning ?? false;

    const calibratedGrainTempF = useMemo(() => {
        if (!isReconditioningActive) return null;
        const base = props.binDto?.reconditioningOperation?.lastSampleTemperatureF;
        if (base == null) return null;
        return base + (values?.grainTempCalibrationF ?? 0);
    }, [props.binDto?.reconditioningOperation?.lastSampleTemperatureF, isReconditioningActive, values?.grainTempCalibrationF]);

    const calibratedMcPercent = useMemo(() => {
        if (!isReconditioningActive) return null;
        const base = props.binDto?.reconditioningOperation?.lastSampleMcPercent;
        if (base == null) return null;
        return base + (values?.grainMcCalibrationPercent ?? 0);
    }, [props.binDto?.reconditioningOperation?.lastSampleMcPercent, isReconditioningActive, values?.grainMcCalibrationPercent]);

    const calibratedPlenumTempF = useMemo(() => {
        if (!isReconditioningActive) return null;
        const base = props.binDto?.averagePlenumRhtSensor_TemperatureF;
        if (base == null) return null;
        return base + (values?.plenumTempCalibrationF ?? 0);
    }, [props.binDto?.averagePlenumRhtSensor_TemperatureF, isReconditioningActive, values?.plenumTempCalibrationF]);

    const formatDisplayValue = (value: number | null | undefined, suffix: string) =>
        value == null ? `--${suffix}` : `${value}${suffix}`;

    const handleIncreaseGrainTemp = () => {
        const current = form.getFieldValue("grainTempCalibrationF") ?? 0;
        form.setFieldValue("grainTempCalibrationF", current + tempButtonStep);
    };

    const handleDecreaseGrainTemp = () => {
        const current = form.getFieldValue("grainTempCalibrationF") ?? 0;
        form.setFieldValue("grainTempCalibrationF", current - tempButtonStep);
    };

    const handleIncreaseGrainMc = () => {
        const current = form.getFieldValue("grainMcCalibrationPercent") ?? 0;
        form.setFieldValue("grainMcCalibrationPercent", current + 2);
    };

    const handleDecreaseGrainMc = () => {
        const current = form.getFieldValue("grainMcCalibrationPercent") ?? 0;
        form.setFieldValue("grainMcCalibrationPercent", current - 2);
    };

    const handleIncreasePlenumTemp = () => {
        const current = form.getFieldValue("plenumTempCalibrationF") ?? 0;
        form.setFieldValue("plenumTempCalibrationF", current + tempButtonStep);
    };

    const handleDecreasePlenumTemp = () => {
        const current = form.getFieldValue("plenumTempCalibrationF") ?? 0;
        form.setFieldValue("plenumTempCalibrationF", current - tempButtonStep);
    };

    return(
        <Modal
        {...props}
        title={<p>Reconditioning Settings</p>}
        destroyOnClose
        footer={null}
        width="100%"
        style={{ maxWidth: 800 }}
        >
            <Spin spinning={isLoading}>
                <Form form={form} onFinish={onFinish}>
                    <Row justify={"start"}>
                        <Title style={{ margin: "0px", paddingLeft: "10px", paddingTop: "10px" }} level={3}>
                            Adjust Reconditioning Settings
                        </Title>
                    </Row>

                    <Form.Item hidden name={"targetStorageBinNumber"} />

                    <br/>
                    <Row gutter={10} justify={'center'} style={{ margin: "5px" }}>
                        <Col span={8}>
                            <Form.Item name={"samplesCntInAverage"} label={"Num samp in avg:"}>
                                <InputNumber controls={false} inputMode="decimal" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name={"targetMcPercent"} label={"Target Moist:"}>
                                <InputNumber controls={false} inputMode="decimal" suffix="%" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <br/>

                    <Row justify={"start"} gutter={8} style={{ paddingLeft: "10px" }}>
                        <Col>
                            <Form.Item
                            name={"useTimer"}
                            label={<Text>Use Fan Timer</Text>}
                            style={{ margin: 0, padding: 0 }}
                            >
                                <Radio.Group buttonStyle={"solid"} optionType="button">
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        {values?.useTimer && (
                            <Col>
                                <Row gutter={8}>
                                    <Col>
                                        <Form.Item name={"runFanHours"} label={<Text>Enter Duration</Text>} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} max={999} min={0} inputMode="decimal" placeholder="Hours" suffix="hr" />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <Form.Item name={"runFanMinutes"} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} max={999} min={0} inputMode="decimal" placeholder="Minutes" suffix="min" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    <br/>

                    <Row justify={"start"} gutter={8} style={{ paddingLeft: "10px" }}>
                        <Col>
                            <Form.Item
                            name={"useEmcRange"}
                            label={<Text>Use EMC Set Point</Text>}
                            style={{ margin: 0, padding: 0 }}
                            >
                                <Radio.Group buttonStyle={"solid"} optionType="button">
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        {values?.useEmcRange && (
                            <Col>
                                <Row gutter={8}>
                                    <Col>
                                        <Form.Item name={"minWeatherMc"} label={<Text>Enter EMC Range (%)</Text>} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} inputMode="decimal" max={100} min={0} placeholder="Min" />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <Form.Item name={"maxWeatherMc"} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} inputMode="decimal" max={100} min={0} placeholder="Max" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    <br/>

                    <Row justify={"start"} gutter={8} style={{ paddingLeft: "10px" }}>
                        <Col>
                            <Form.Item
                            name={"useTempRange"}
                            label={<Text>Use Temp Set Point</Text>}
                            style={{ margin: 0, padding: 0 }}
                            >
                                <Radio.Group buttonStyle={"solid"} optionType="button">
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        {values?.useTempRange && (
                            <Col>
                                <Row gutter={8}>
                                    <Col>
                                        <Form.Item name={"minWeatherTemperatureF"} label={<Text>{`Enter Temp Range (${temperatureUnit})`}</Text>} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} inputMode="decimal" max={200} min={0} placeholder="Min" />
                                        </Form.Item>
                                    </Col>
                                    <Col>
                                        <Form.Item name={"maxWeatherTemperatureF"} style={{ margin: 0, padding: 0 }}>
                                            <InputNumber controls={false} inputMode="decimal" max={200} min={0} placeholder="Max" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    <br/>

                    <Row style={{ paddingLeft:'10px'}}>
                        <Col span={13}>
                            <Form.Item
                            name={"disableMoistureChangingCheck"}
                            label={<Text>Disable Check for Moisture Not Changing</Text>}
                            valuePropName="checked"
                            style={{ margin:0, padding:0 }}
                            >
                                <Radio.Group buttonStyle={'solid'} defaultValue={false} optionType="button">
                                    <Radio value={false}>NO</Radio>
                                    <Radio value={true}>YES</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify={'center'}>
                        <Col>
                            <Form.Item
                            labelCol={{ span: 24}}
                            style={{ margin: 0, padding: 0}}
                            label={'Grain Type'}
                            name={"grainType"}
                            className="form-item"
                            >
                                <Select
                                style={{ width:"250px" }}
                                dropdownStyle={{
                                    borderRadius:'0px',
                                    backgroundColor: 'white'
                                }}
                                >
                                    {grainOptions.map((gt) => (
                                        <Option key={gt.value} value={gt.value}>
                                            <span className="select-fantype-text">{getGrainTypeStr(gt.label)}</span>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Row justify={'center'}>
                        <Col span={20} style={{ textAlign: 'center' }}>
                            <Text>
                                Adjust the grain temp cal, moisture cal, and the plenum temp cal by tapping up/down. the calibration will be shown on the right and the adjusted value will be on the left
                            </Text>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Grain Temp:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!isReconditioningActive ? "--" : formatDisplayValue(calibratedGrainTempF, temperatureUnit)}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreaseGrainTemp}>GRAIN DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreaseGrainTemp}>GRAIN UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="grainTempCalibrationF" hidden />
                                    <p>{formatDisplayValue(values?.grainTempCalibrationF, temperatureUnit)}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Moisture:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!isReconditioningActive ? "--" : formatDisplayValue(calibratedMcPercent, "%")}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreaseGrainMc}>MOIST DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreaseGrainMc}>MOIST UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="grainMcCalibrationPercent" hidden />
                                    <p>{formatDisplayValue(values?.grainMcCalibrationPercent, "%")}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify="center" gutter={10} style={{ padding: "4px" }}>
                        <Col span={4}>
                            <div className={"non-input-bottom-row"}>Plenum Temp:</div>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                {!isReconditioningActive ? "--" : formatDisplayValue(calibratedPlenumTempF, temperatureUnit)}
                            </div>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleDecreasePlenumTemp}>PLENUM TEMP DOWN</Button>
                        </Col>
                        <Col span={6}>
                            <Button type="primary" block onClick={handleIncreasePlenumTemp}>PLENUM TEMP UP</Button>
                        </Col>
                        <Col span={3}>
                            <div style={{ borderRadius: "9999px", fontFamily: "monospace" }} className={"non-input-bottom-row"}>
                                <>
                                    <Form.Item name="plenumTempCalibrationF" hidden />
                                    <p>{formatDisplayValue(values?.plenumTempCalibrationF, temperatureUnit)}</p>
                                </>
                            </div>
                        </Col>
                    </Row>

                    <Row justify={"end"} gutter={12}>
                        <Col>
                            <Button onClick={handleClose}>Cancel</Button>
                        </Col>
                        <Col>
                            <Form.Item>
                                <Button htmlType="submit" type="primary">
                                    Submit
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </Modal>
    )
}

export default PremierPlusReconditioningSettingsModal;
